import { Capacitor } from '@capacitor/core';
import { useEffect, useState } from 'react';
import { navigate } from '../../app/navigation';
import { useT, type MessageKey } from '../../i18n/i18n';
import {
  billingStore,
  checkOfferCodeResult,
  ensureBillingStarted,
  openManageSubscriptions,
  priceLabel,
  purchaseAddOn,
  purchaseAnnual,
  purchaseMonthly,
  purchaseSingle,
  redeemCodeAction,
  redeemOfferCode,
  refreshBilling,
  restorePurchases,
  type PurchaseOptions,
  type PurchaseResult
} from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import { normalizeRedeemCode, type BillingProductKey, type RedeemOutcome } from '../../../supabase/functions/_shared/billingPolicy';
import { Banner, Button, Card, CheckRow, InlineMessage, SectionHeader, TextInput } from '../../ui/components';
import { formatClock, formatDate, useOnlineStatus } from '../../ui/hooks';
import '../../ui/billing.css';
import { storeKey } from '../billing/storeCopy';
import { AutoRenewConsent } from '../billing/AutoRenewConsent';

// App Store Review 3.1.2: an app selling auto-renewable subscriptions links its Terms of Use (EULA) in the app,
// next to the subscription terms. Since 2026-10-20 that is the product's own Terms of Service (Owner decision
// list item 5; the App Store listing's custom EULA carries the same text) and it is linked on every platform
// (item 6: Android and the web had no terms link before).
import { TERMS_OF_SERVICE_URL } from './legalLinks';

// Settings → Plan & Subscription (frozen Figma "Settings — Plan & subscription" plus its
// "Code applied" / "Invalid code" states). Owner decisions 2026-09-11:
//   D-3  the add-on pack is offered to monthly / annual subscribers only
//   D-4  the allowance is shown as "about N inspections left"
//   D-5  with no plan, a formal inspection costs one single purchase
//   X-2  the terms say plainly that an allowance does not carry over
//   X-5  the promo-code entry opens APPLE's redemption page — this app never unlocks paid content
//        with a code of its own (App Store Review 3.1.1)
//   X-6  current plan · about N left · restore purchases · subscription terms · manage subscription
// Every purchase happens in Apple's own sheet: there is no card form, no discount arithmetic, and
// the four prices on this screen never change (they are constants in billingPolicy.ts).
const PRODUCTS: ReadonlyArray<{ key: BillingProductKey; nameKey: MessageKey; bodyKey: MessageKey }> = Object.freeze([
  { key: 'single', nameKey: 'settings.planSingle', bodyKey: 'billing.singleBody' },
  { key: 'monthly', nameKey: 'settings.planMonthly', bodyKey: 'billing.monthlyBody' },
  { key: 'annual', nameKey: 'settings.planAnnual', bodyKey: 'billing.annualBody' },
  { key: 'addOn', nameKey: 'settings.planAddon', bodyKey: 'billing.addOnBody' }
]);

// App Store Review 3.1.2: the billing period sits beside the price.
export const PERIOD_KEYS: Readonly<Record<BillingProductKey, MessageKey>> = Object.freeze({
  single: 'billing.periodOnce',
  monthly: 'billing.periodMonthly',
  annual: 'billing.periodAnnual',
  addOn: 'billing.periodOnce'
});

// A successful redemption says how many 单 it added (billing.redeemDone, singular / plural).
const REDEEM_MESSAGES: Readonly<Record<Exclude<RedeemOutcome, 'redeemed'>, MessageKey>> = Object.freeze({
  invalid: 'billing.redeemInvalid',
  expired: 'billing.redeemExpired',
  alreadyRedeemed: 'billing.redeemAlready',
  usedUp: 'billing.redeemUsedUp'
});

// Owner decision list 2026-10-19 item 46 (P-6): the redemption code is entered on the WEB build only.
// An iOS / Android app never unlocks anything with a code of its own (App Store Review 3.1.1), and it
// never points to the web for it either.
export function redeemOnThisPlatform(): boolean {
  return Capacitor.getPlatform() === 'web';
}

function RedeemCard() {
  const { t, tp } = useT();
  const busy = useStore(billingStore, (s) => s.busy);
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [consentMissing, setConsentMissing] = useState(false);

  async function redeem() {
    setMessage(null);
    if (!consent) {
      setConsentMissing(true);
      return;
    }
    const result = await redeemCodeAction(code, consent);
    if (result.kind === 'failed') setMessage({ kind: 'err', text: t('billing.redeemFailed') });
    else if (result.outcome === 'redeemed') setMessage({ kind: 'ok', text: tp('billing.redeemDone', result.units) });
    else setMessage({ kind: 'err', text: t(REDEEM_MESSAGES[result.outcome], { n: result.units }) });
    if (result.kind === 'done' && result.outcome === 'redeemed') setCode('');
  }

  return (
    <Card testId="plan-redeem-card">
      <SectionHeader title={t('billing.redeemTitle')} />
      <p className="card-note">{t('billing.redeemBody')}</p>
      {/* Every message sits right under what it is about (Owner rule 9): the code's answer under the
          code, the missing tick under the tick. */}
      <div className="form-stack">
        <TextInput id="plan-redeem-code" value={code} onChange={(value) => setCode(value)} placeholder={t('billing.redeemPlaceholder')} testId="plan-redeem-code" />
        {message ? <InlineMessage kind={message.kind} text={message.text} /> : null}
        <CheckRow
          label={t('billing.redeemConsent')}
          checked={consent}
          onChange={(value) => {
            setConsent(value);
            if (value) setConsentMissing(false);
          }}
          testId="plan-redeem-consent"
        />
        {consentMissing ? <InlineMessage kind="err" text={t('billing.redeemNeedConsent')} /> : null}
      </div>
      <div className="card-actions">
        <Button variant="secondary" icon="key" disabled={busy !== null || normalizeRedeemCode(code) === null} onClick={() => void redeem()} testId="plan-redeem">
          {t('billing.redeemAction')}
        </Button>
      </div>
    </Card>
  );
}

const PURCHASE_ACTIONS: Record<BillingProductKey, (options?: PurchaseOptions) => Promise<PurchaseResult>> = {
  single: purchaseSingle,
  monthly: purchaseMonthly,
  annual: purchaseAnnual,
  addOn: purchaseAddOn
};

const FAILURE_KEYS: Record<string, MessageKey> = Object.freeze({
  notConfigured: 'billing.notConfigured',
  notNative: 'billing.notConfigured',
  notEligible: 'billing.notEligible',
  offline: 'billing.offline',
  notSignedIn: 'billing.notSignedIn',
  network: 'billing.offline',
  productUnavailable: 'billing.purchaseFailed',
  notAllowed: 'billing.purchaseFailed',
  identity: 'billing.notSignedIn',
  unknown: 'billing.purchaseFailed',
  consentRequired: 'billing.autoRenewNeedConsent',
  consentNotRecorded: 'billing.consentNotRecorded'
});

export function isSubscriptionKey(key: BillingProductKey): boolean {
  return key === 'monthly' || key === 'annual';
}

export function purchaseMessage(result: PurchaseResult | null): { kind: 'ok' | 'err' | 'info'; key: MessageKey } | null {
  if (!result) return null;
  if (result.kind === 'purchased') return { kind: 'ok', key: 'billing.purchaseDone' };
  if (result.kind === 'pending') return { kind: 'info', key: 'billing.purchasePending' };
  if (result.kind === 'cancelled') return { kind: 'info', key: 'billing.purchaseCancelled' };
  return { kind: 'err', key: FAILURE_KEYS[result.reason] ?? 'billing.purchaseFailed' };
}

const SERVER_STATE_KEYS: Record<string, MessageKey> = Object.freeze({
  offline: 'billing.serverOffline',
  unavailable: 'billing.serverUnavailable',
  notConfigured: 'billing.serverNotConfigured',
  unauthenticated: 'billing.notSignedIn'
});

export function PlanPage() {
  const { t, tp } = useT();
  // The store the wording names: Google Play on Android, the approved App Store wording elsewhere.
  const ts = (key: MessageKey) => t(storeKey(key));
  const online = useOnlineStatus();
  const state = useStore(billingStore, (s) => s);
  const [restoreMessage, setRestoreMessage] = useState<MessageKey | null>(null);
  // Web version (item 39): the automatic-renewal tick the subscription buttons need.
  const web = state.storePlatform === 'web';
  const [renewConsent, setRenewConsent] = useState(false);
  const [renewMissing, setRenewMissing] = useState(false);

  function buy(product: BillingProductKey) {
    if (web && isSubscriptionKey(product) && !renewConsent) {
      setRenewMissing(true);
      return;
    }
    void PURCHASE_ACTIONS[product]({ autoRenewConsent: renewConsent });
  }

  // Opening the page asks again when the last answer was not a good one (emulator walkthrough
  // 2026-10-16: an earlier failure's note stayed on this page until the app was restarted).
  useEffect(() => {
    void (async () => {
      const wasStarted = billingStore.get().started;
      await ensureBillingStarted();
      if (wasStarted && billingStore.get().serverState !== 'ok') await refreshBilling();
    })();
  }, []);

  const purchase = purchaseMessage(state.lastPurchase);
  const serverNote = SERVER_STATE_KEYS[state.serverState] ?? null;
  const canPurchase = state.storeConfigured && online && state.busy === null;

  async function handleRestore() {
    setRestoreMessage(null);
    const outcome = await restorePurchases();
    if (outcome.kind === 'failed') setRestoreMessage(FAILURE_KEYS[outcome.reason] ?? 'billing.purchaseFailed');
    else setRestoreMessage(outcome.plan === null && outcome.singlePurchases === 0 ? 'billing.restoreEmpty' : 'billing.restoreDone');
  }

  return (
    <>
      {state.storePlatform === 'fake' ? <Banner tone="warning" icon="info" title={t('billing.simulated')} testId="billing-simulated" /> : null}
      {/* Owner device test 2026-09-21 (build 92 shipped without the store key): every buy button was
          grey and nothing said why. Whenever purchases cannot work in this build — no store on this
          platform, or the iOS app built without its store key — the page says so. */}
      {state.storePlatform === 'unavailable' || (state.started && !state.storeConfigured && state.storePlatform !== 'fake') ? (
        <Banner tone="info" icon="info" title={t('billing.notConfigured')} body={ts('billing.notConfiguredBody')} testId="billing-unavailable" />
      ) : null}

      <Card testId="plan-status-card">
        <SectionHeader title={t('billing.currentPlan')} />
        <div className="kv">
          <span className="kv-key">{t('settings.plan')}</span>
          <span className="kv-val" data-testid="plan-name">
            {state.plan === 'monthly' ? t('settings.planMonthly') : state.plan === 'annual' ? t('settings.planAnnual') : t('billing.planNone')}
          </span>
        </div>
        {state.plan !== null && state.activeUntil !== null ? (
          <p className="card-note" data-testid="plan-renewal">
            {state.willRenew === false ? t('billing.endsOn', { date: formatDate(state.activeUntil) }) : t('billing.renewsOn', { date: formatDate(state.activeUntil) })}
          </p>
        ) : null}
        {/* Android acceptance 2026-10-21 (issue 20): an internal test account is never charged a 单, so
            「还剩 0 单 · AI 单数已用完」 was untrue for it. */}
        <p className="billing-figure" data-testid="plan-remaining">
          {state.internalTester ? t('billing.internalTester') : tp('billing.remainingInspections', state.remainingInspections)}
        </p>
        {/* Owner 2026-10-19 (decision list items 35 / 36): the inspector sees 「还剩几单」 — the period's
            单 and the pack / gift 单 — never money. */}
        {state.server && state.server.poolUnits > 0 ? (
          <p className="card-note" data-testid="plan-pool-line">
            {t('billing.poolLine', { left: state.server.poolRemainingUnits, total: state.server.poolUnits })}
          </p>
        ) : null}
        {state.server && state.server.addOnRemainingUnits > 0 ? (
          <p className="card-note" data-testid="plan-extra-line">
            {t('billing.extraLine', { n: state.server.addOnRemainingUnits })}
          </p>
        ) : null}
        <p className="billing-figure-sub" data-testid="plan-remaining-note">
          {state.internalTester ? t('billing.internalTesterNote') : state.remainingInspections > 0 ? t('billing.remainingNote') : t('billing.noAllowance')}
        </p>
        {/* Owner 2026-09-11 「头三次免费」: what this account still has for free, stated plainly. */}
        {state.trialRemaining > 0 ? (
          <p className="card-note" data-testid="plan-trial-remaining">
            {tp('billing.trialRemaining', state.trialRemaining)}
          </p>
        ) : null}
        {state.singleCredits > 0 ? (
          <p className="card-note" data-testid="plan-single-credits">
            {tp('billing.singleCredits', state.singleCredits)}
          </p>
        ) : null}
        {state.unfunded.length > 0 ? <InlineMessage kind="err" text={tp('billing.unfunded', state.unfunded.length)} /> : null}
        {state.lastSyncedAt !== null ? (
          <p className="card-note" data-testid="plan-last-synced">
            {t('billing.lastSynced', { time: formatClock(state.lastSyncedAt) })}
          </p>
        ) : null}
        {serverNote ? <InlineMessage kind="info" text={t(serverNote)} /> : null}
      </Card>

      <Card testId="plan-list-card">
        <SectionHeader title={t('settings.planOptions')} />
        {/* Owner 2026-10-19 item 38: the first line of the price list. */}
        <p className="billing-free-first" data-testid="plan-free-first">
          {t('billing.freeFirst')}
        </p>
        {web ? (
          <AutoRenewConsent
            checked={renewConsent}
            missing={renewMissing}
            onChange={(value) => {
              setRenewConsent(value);
              if (value) setRenewMissing(false);
            }}
            testId="plan-auto-renew"
          />
        ) : null}
        {PRODUCTS.filter((product) => product.key !== 'addOn' || state.canBuyAddOn).map((product) => (
          <div className="billing-plan" key={product.key} data-testid={`plan-option-${product.key}`}>
            <div className="billing-plan-head">
              <span className="billing-plan-name">{t(product.nameKey)}</span>
              <span className="billing-plan-price" data-testid={`plan-price-${product.key}`}>
                {priceLabel(product.key)}
                <span className="billing-plan-period">{t(PERIOD_KEYS[product.key])}</span>
              </span>
            </div>
            <p className="billing-plan-body">{t(product.bodyKey)}</p>
            <Button variant="secondary" icon="credit_card" disabled={!canPurchase} onClick={() => buy(product.key)} testId={`plan-buy-${product.key}`}>
              {state.busy === product.key ? ts('billing.buying') : ts('billing.buy')}
            </Button>
          </div>
        ))}
        {purchase ? <InlineMessage kind={purchase.kind} text={ts(purchase.key)} /> : null}
        {!online ? <InlineMessage kind="info" text={t('billing.offline')} /> : null}
      </Card>

      {redeemOnThisPlatform() ? <RedeemCard /> : null}

      {/* Apple / Google offer codes are a store feature: not on the web version, which has its own codes. */}
      {web ? null : (
      <Card testId="plan-offer-code-card">
        <SectionHeader title={t('billing.offerCode')} />
        <p className="card-note">{ts('billing.offerCodeBody')}</p>
        <div className="card-actions">
          <Button variant="secondary" icon="key" disabled={!canPurchase} onClick={() => void redeemOfferCode()} testId="plan-offer-code">
            {t('billing.offerCode')}
          </Button>
          {state.offerCode === 'waiting' || state.offerCode === 'notDetected' ? (
            // Apple can finish a redemption after the sheet closes, so checking again stays offered.
            <Button variant="text" icon="refresh" onClick={() => void checkOfferCodeResult()} testId="plan-offer-code-check">
              {t('billing.offerCodeCheck')}
            </Button>
          ) : null}
        </div>
        {state.offerCode === 'waiting' ? <InlineMessage kind="info" text={ts('billing.offerCodeWaiting')} /> : null}
        {state.offerCode === 'applied' ? <InlineMessage kind="ok" text={t('billing.offerCodeApplied')} /> : null}
        {state.offerCode === 'notDetected' ? <InlineMessage kind="err" text={ts('billing.offerCodeNotDetected')} /> : null}
      </Card>
      )}

      <Card testId="plan-actions-card">
        <SectionHeader title={t('billing.manage')} />
        <p className="card-note">{ts('billing.manageBody')}</p>
        {restoreMessage ? <InlineMessage kind={restoreMessage === 'billing.restoreDone' ? 'ok' : 'info'} text={ts(restoreMessage)} /> : null}
        <div className="card-actions">
          <Button variant="secondary" icon="restart_alt" disabled={!state.storeConfigured || !online || state.busy !== null} onClick={() => void handleRestore()} testId="plan-restore">
            {t('billing.restore')}
          </Button>
          <Button variant="secondary" icon="open_in_full" disabled={!state.storeConfigured || (web && state.managementUrl === null)} onClick={() => openManageSubscriptions()} testId="plan-manage">
            {t('billing.manage')}
          </Button>
        </div>
      </Card>

      <Card testId="plan-terms-card">
        <SectionHeader title={t('billing.terms')} />
        <ul className="billing-terms">
          {(['billing.terms1', 'billing.terms2', 'billing.terms3', 'billing.terms4', 'billing.terms5', 'billing.terms6'] as MessageKey[]).map((key) => (
            <li key={key}>{ts(key)}</li>
          ))}
        </ul>
        <div className="card-actions">
          <Button variant="text" icon="gavel" onClick={() => navigate({ screen: 'settingsPage', page: 'terms' })} testId="plan-terms-link">
            {t('settings.terms')}
          </Button>
          <Button variant="text" icon="privacy_tip" onClick={() => navigate({ screen: 'settingsPage', page: 'privacy' })} testId="plan-privacy-link">
            {t('settings.privacy')}
          </Button>
          {/* Capacitor hands a window.open of a foreign URL to the OS, which opens the system browser. */}
          <Button variant="text" icon="description" onClick={() => window.open(TERMS_OF_SERVICE_URL, '_blank')} testId="plan-eula-link">
            {t('billing.eula')}
          </Button>
        </div>
      </Card>
    </>
  );
}
